import React, { FC, useEffect, useState } from "react";
import { Flex, Icon, Link, Text, Heading, Container as TeddyContainer, Box } from "@telia/teddy";
import { Container } from "../../../components";
import _get from "lodash/get";
import Confetti from "react-dom-confetti";
import { connect, useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { CUSTOMER_TYPE, DELIVERY_TYPE, DeliveryType, SIM_PICK_UP_IN_STORE_SKU } from "../../../../../constants";
import { DeliveryMethod, DynamicPage, RootState, StepIndicatorType } from "../../../../../models";
import { API_URL_GET_ORDER_SUMMARY } from "../../api";
import { getConfig, getOrderId } from "../../../app/state/selectors";
import { fetchPickupPoints } from "../../../pickup-points/state/actions";
import { fetchShopAvailabilityBySku } from "../../../shop-picker/state/actions";
import CheckoutPage from "../../components/organisms/CheckoutPage/CheckoutPage";
import { hasExternalLegalOwner as gotExternalLegalOwner } from "../../state/selectors/accountSelectors";
import {
    getTotalPriceUpfront,
    hasAnyTeliaTryggServices,
    hasAnyTeliaTryggSubscriptions,
} from "../../state/selectors/cartSelectors";
import {
    getDeliveryInformation,
    isDigitalDelivery as isDigitalDeliverySelector,
} from "../../state/selectors/combinedSelectors";
import * as CUSTOMER_INFO_SELECTORS from "../../state/selectors/customerInfoSelectors";
import {
    getContactInfoEmail,
    getDeliveryInfoType,
    getDeliveryInfoZipCode,
    getEnhancedDeliveryInfoSelected,
} from "../../state/selectors/deliveryInfoSelectors";
import getStepIndicatorData from "../../state/selectors/getStepIndicatorData";
import { getPhoneNumberCheckType } from "../../state/selectors/phoneNumberSelectors";
import {
    getLegalSubscriptionId,
    hasSubscriptions as hasSubscriptionsSelector,
    isSubscriptionBroadband,
} from "../../state/selectors/subscriptionsSelectors";
import { isDeliveryTypeTracked as deliveryTypeTracked } from "../../utils";
import DynamicBlocks from "../../../components/components/organisms/DynamicBlocks/DynamicBlocks";
import ReceiptOrderDetailsCard from "./ReceiptOrderDetailsCard";
import "./Receipt.less";
import { getDynamicPages } from "../../state/selectors/dynamicPagesSelectors";
import { ConnectedShoppingCart } from "../../../navigation";
import ReceiptNextSteps from "./ReceiptNextSteps";
import CartAccordion from "./CartAccordion";
import AppLinks from "../../../components/components/molecules/AppLinks/AppLinks";
import { TeliaTryggCard } from "../../../navigation/components/organisms/ConnectedShoppingCart/ConnectedShoppingCart";

const getPageIndex = ({
    isUpfront,
    stepIndicator,
    hasExternalLegalOwner,
    hasSubscriptions,
    isMBB,
}: {
    isUpfront: number;
    stepIndicator: StepIndicatorType;
    hasExternalLegalOwner: boolean;
    hasSubscriptions: boolean;
    isMBB: boolean;
}): number => {
    if (isUpfront) {
        return stepIndicator.steps;
    }
    if (hasExternalLegalOwner) {
        return 5;
    }
    if (hasSubscriptions && !isMBB) {
        return 4;
    }
    return 3;
};

const CONFETTI_CONFIG = {
    angle: 90,
    spread: 360,
    startVelocity: 40,
    elementCount: 70,
    dragFriction: 0.12,
    duration: 3000,
    stagger: 3,
    width: "10px",
    height: "10px",
    perspective: "800px",
    colors: ["#a864fd", "#29cdff", "#78ff44", "#ff718d", "#fdff6a"],
};

const ReceiptTopSection = ({
    deliveryInfoContactEmail,
    orderRef,
}: {
    deliveryInfoContactEmail: string;
    orderRef?: string;
}) => (
    <div className="receipt-container__confirmation">
        <Icon
            className="receipt-container__check-icon"
            name="check-circle"
            size="xl"
            aria-hidden="true"
            focusable="false"
        />
        <Heading mb="100" as="h1" variant="display-200" className="receipt-container__confirmation-title">
            Bestillingen er gjennomført
        </Heading>
        <Text as="div" variant="paragraph-100" className="receipt-container__confirmation-subtitle" data-di-mask>
            Ordrebekreftelse sendes til {deliveryInfoContactEmail}
            {orderRef && (
                <Link
                    className="download-shoppingcart-button"
                    href={API_URL_GET_ORDER_SUMMARY}
                    rel="noopener noreferrer"
                    target="_blank"
                    variant="navigation"
                    aria-label="Last ned kvittering (PDF) (åpnes i nytt vindu)"
                    style={{ marginTop: "var(--teddy-spacing-200)" }}
                >
                    Last ned kvittering (PDF)
                </Link>
            )}
        </Text>
    </div>
);

interface StateProps {
    deliveryInfoContactEmail: string;
    deliveryInfoType?: DeliveryType;
    deliveryInfoZipCode?: string;
    hasExternalLegalOwner: boolean;
    hasSubscriptions: boolean;
    isDeliveryTypeTracked: boolean;
    isDigitalDelivery: boolean;
    isMBB: boolean;
    legalCustomerType?: string;
    legalOwnerPostcode: string;
    orderId?: string;
    selectedDeliveryMethod?: DeliveryMethod;
    shouldShowTeliaTryggInfo?: boolean;
    stepIndicator: StepIndicatorType;
    totalPriceUpfront: number;
    dynamicPages: DynamicPage[];
    deliveryInformation: { title: string; description: string };
    minsideUrl: string;
}

const Receipt: FC<StateProps> = ({
    deliveryInfoContactEmail,
    deliveryInfoType,
    deliveryInfoZipCode,
    isDeliveryTypeTracked,
    isDigitalDelivery,
    isMBB,
    legalOwnerPostcode,
    legalCustomerType = "",
    orderId = "",
    hasSubscriptions,
    hasExternalLegalOwner,
    selectedDeliveryMethod,
    stepIndicator,
    totalPriceUpfront,
    shouldShowTeliaTryggInfo,
    dynamicPages,
    deliveryInformation,
    minsideUrl,
}) => {
    const isUpfront = totalPriceUpfront + _get(selectedDeliveryMethod, "price", 0);
    const location = useLocation();
    const { orderRef = "" } = useParams<{ orderRef: string }>();
    const dispatch = useDispatch();

    const onSignIn = async () => {
        window.location.href = minsideUrl;
    };

    const [confettiTrigger, setConfettiTrigger] = useState(false);
    const pickUpPointsZipCode = deliveryInfoZipCode || legalOwnerPostcode;

    useEffect(() => {
        setTimeout(() => setConfettiTrigger(true), 500);
    }, []);

    useEffect(() => {
        switch (deliveryInfoType) {
            case DELIVERY_TYPE.PICK_UP_IN_SHOP:
                dispatch(fetchShopAvailabilityBySku(SIM_PICK_UP_IN_STORE_SKU));
                break;
            case DELIVERY_TYPE.PICK_UP_POINT:
                dispatch(fetchPickupPoints(pickUpPointsZipCode));
                break;
        }
    }, [deliveryInfoType, pickUpPointsZipCode, fetchPickupPoints, fetchShopAvailabilityBySku]);

    const pageIndex = getPageIndex({ isUpfront, stepIndicator, hasExternalLegalOwner, hasSubscriptions, isMBB });

    return (
        <CheckoutPage
            isFetchingData={false}
            pageIndex={pageIndex}
            stepIndicator={{ ...stepIndicator, hide: true }}
            path={location.pathname}
            shouldOnlyShowSlideoutCart={true}
            topSection={<ReceiptTopSection deliveryInfoContactEmail={deliveryInfoContactEmail} orderRef={orderRef} />}
        >
            <CartAccordion />
            {shouldShowTeliaTryggInfo && (
                <Box className="receipt-container-trygg">
                    <TeliaTryggCard />
                </Box>
            )}
            <Confetti active={confettiTrigger} config={CONFETTI_CONFIG} />
            <Container size="large" className="receipt-container" noPadding>
                <Flex gap={{ sm: "0", lg: "300" }} align="start" width="100%" className="receipt-container__layout">
                    <Flex direction="column" gap={{ sm: "100", lg: "300" }} className="receipt-container__column">
                        {dynamicPages?.map((page) => (
                            <div key={`page-${page.id}`} className="receipt-container__dynamic-page">
                                {page.sectionBlocks.map((block, index) => (
                                    <section key={`section-block-${page.id}-${index}`}>
                                        {block.heading && (
                                            <TeddyContainer
                                                maxWidth={{
                                                    lg: "660px",
                                                }}
                                                className="receipt-container__section-block__heading-container"
                                            >
                                                <Heading mb="100" as="h2" variant="title-400">
                                                    {block.heading}
                                                </Heading>
                                            </TeddyContainer>
                                        )}
                                        {block.dynamicContent && block.dynamicContent.length > 0 && (
                                            <DynamicBlocks
                                                fullWidth
                                                blocks={block.dynamicContent}
                                                idPrefix={`section-block-${page.id}-${index}`}
                                                shouldRenderLoginCard
                                                isWhiteBg={false}
                                                trackingContext={{ prefix: "receipt_page", url: location.pathname }}
                                                stepIndicatorList={<ReceiptNextSteps />}
                                                orderDetailsCard={
                                                    <ReceiptOrderDetailsCard
                                                        orderId={orderId}
                                                        deliveryTitle={deliveryInformation.title}
                                                        deliveryDescription={deliveryInformation.description}
                                                        deliveryInfoType={deliveryInfoType}
                                                        isDigitalDelivery={isDigitalDelivery}
                                                        showTrackingInfo={
                                                            isDeliveryTypeTracked &&
                                                            legalCustomerType !== CUSTOMER_TYPE.EXISTING &&
                                                            !isDigitalDelivery
                                                        }
                                                    />
                                                }
                                                onSignIn={onSignIn}
                                            />
                                        )}
                                    </section>
                                ))}
                            </div>
                        ))}
                    </Flex>
                    <Flex
                        direction="column"
                        gap={{ sm: "100", lg: "300" }}
                        className="receipt-container__column receipt-container__cart-column"
                    >
                        <div className="receipt-container__cart-wrapper">
                            <ConnectedShoppingCart
                                useStandalone
                                heading="Din bestilling"
                                shouldShowTeliaTryggInfo={shouldShowTeliaTryggInfo}
                            />
                        </div>
                    </Flex>
                </Flex>
            </Container>
            <TeddyContainer width="100%">
                <AppLinks size="large" className="receipt-container__app-links" />
            </TeddyContainer>
        </CheckoutPage>
    );
};

const mapStateToProps = (state: RootState): StateProps => {
    const legalSubscriptionId = getLegalSubscriptionId(state);

    return {
        orderId: getOrderId(state),
        deliveryInformation: getDeliveryInformation(state),
        legalOwnerPostcode: CUSTOMER_INFO_SELECTORS.getLegalOwnerPostcode(state),
        legalCustomerType: getPhoneNumberCheckType(state, legalSubscriptionId),
        deliveryInfoContactEmail: getContactInfoEmail(state),
        isDeliveryTypeTracked: deliveryTypeTracked(getDeliveryInfoType(state)),
        deliveryInfoType: getDeliveryInfoType(state),
        deliveryInfoZipCode: getDeliveryInfoZipCode(state),
        isMBB: isSubscriptionBroadband(state, legalSubscriptionId),
        hasSubscriptions: hasSubscriptionsSelector(state),
        hasExternalLegalOwner: gotExternalLegalOwner(state),
        selectedDeliveryMethod: getEnhancedDeliveryInfoSelected(state),
        isDigitalDelivery: isDigitalDeliverySelector(state),
        stepIndicator: getStepIndicatorData(state),
        totalPriceUpfront: getTotalPriceUpfront(state),
        shouldShowTeliaTryggInfo: hasAnyTeliaTryggSubscriptions(state) || hasAnyTeliaTryggServices(state),
        dynamicPages: getDynamicPages(state),
        minsideUrl: getConfig(state)?.minsideUrl || "",
    };
};

export default connect(mapStateToProps)(Receipt);
